// @ts-nocheck
import type { AxiosInstance } from 'axios';
import type { QaCheckRow, QaTimecardConfig, QaTimecardDefaults, TimecardScenario } from './types';
import { remapSandboxIds } from './sandboxIds';
import { expectOk } from './http';
import { getWeekStartForDate, formatDate } from './datetime';
import {
  cleanupWeek,
  ensureEmployeeSubLocations,
  ensureEmployeeSchedules,
  ensureEmployeeAppRole,
  ensureEmployeePositions,
  createShift,
  createAttendance,
  hardSync,
  getBreakSettings,
  getTimecard,
  getClockSettings,
} from './seed';
import { summarizeDay } from './assertions';

function personName(emp) {
  return [emp?.firstName, emp?.lastName].filter(Boolean).join(' ').trim() || emp?.email || 'employee';
}

function uniqueNums(values) {
  return [...new Set((values || []).filter((id) => id != null))].sort((a, b) => a - b);
}

function weekStartOnOrBefore(dateStr, exampleWeekStart) {
  const example = new Date(`${exampleWeekStart}T00:00:00`);
  const weekday = example.getDay();
  const d = new Date(`${dateStr}T00:00:00`);
  const diff = (d.getDay() - weekday + 7) % 7;
  d.setDate(d.getDate() - diff);
  return formatDate(d);
}

function datesFromScenario(scenario) {
  const params = scenario.params || {};
  const dates = [];
  if (params.weekStart) dates.push(params.weekStart);
  const seed = params.seed || {};
  for (const row of [...(seed.shifts || []), ...(seed.attendance || [])]) {
    if (row.date) dates.push(row.date);
    if (row.endDate) dates.push(row.endDate);
    if (row.clockOutDate) dates.push(row.clockOutDate);
  }
  return dates;
}

function collectSandboxWeeks(defaults: QaTimecardDefaults, scenarios: TimecardScenario[] = []): string[] {
  const weekStartsOn = defaults.weekStartsOn || 'monday';
  const weeks = new Set();
  if (defaults.weekStart) weeks.add(getWeekStartForDate(defaults.weekStart, weekStartsOn));
  if (defaults.sandboxWeekStart) weeks.add(getWeekStartForDate(defaults.sandboxWeekStart, weekStartsOn));
  for (const scenario of scenarios) {
    for (const date of datesFromScenario(scenario)) {
      weeks.add(getWeekStartForDate(date, weekStartsOn));
    }
  }
  return [...weeks].sort();
}

function walkScenarioIds(node, parentKey, into) {
  if (Array.isArray(node)) {
    if (parentKey === 'ensureSubLocations' || parentKey === 'syncLocations') {
      for (const id of node) {
        if (typeof id === 'number') into.locationIds.add(id);
      }
      return;
    }
    for (const item of node) walkScenarioIds(item, parentKey, into);
    return;
  }
  if (node && typeof node === 'object') {
    for (const [key, value] of Object.entries(node)) {
      walkScenarioIds(value, key, into);
    }
    return;
  }
  if (typeof node !== 'number') return;
  if (parentKey === 'locationId' || parentKey === 'excludeLocationId') into.locationIds.add(node);
  if (parentKey === 'scheduleId') into.scheduleIds.add(node);
  if (parentKey === 'positionId') into.positionIds.add(node);
}

function collectRequiredIds(
  defaults: QaTimecardDefaults,
  scenarios: TimecardScenario[] = [],
  config?: { defaults: QaTimecardDefaults }
) {
  const canonical = config?.defaults || defaults;
  const into = {
    locationIds: new Set(),
    scheduleIds: new Set(),
    positionIds: new Set(),
  };
  for (const id of [defaults.locationId, defaults.locationId2]) {
    if (id != null) into.locationIds.add(id);
  }
  for (const id of [defaults.scheduleId, defaults.scheduleId2]) {
    if (id != null) into.scheduleIds.add(id);
  }
  if (defaults.positionId != null) into.positionIds.add(defaults.positionId);

  for (const scenario of scenarios) {
    const params = remapSandboxIds(scenario.params || {}, canonical, defaults);
    walkScenarioIds(params, null, into);
  }

  return {
    locationIds: [...into.locationIds].sort((a, b) => a - b),
    scheduleIds: [...into.scheduleIds].sort((a, b) => a - b),
    positionIds: [...into.positionIds].sort((a, b) => a - b),
  };
}

function requiredSeedPlan(defaults: QaTimecardDefaults) {
  const plan = [
    {
      id: 'SEED-EMPLOYEE',
      title: 'Sandbox employee',
      path: `/employees/${defaults.employeeId}`,
      nameOf: personName,
    },
    {
      id: 'SEED-LOCATION',
      title: 'Primary location',
      path: `/locations/${defaults.locationId}`,
      nameOf: (row) => row.name,
    },
    {
      id: 'SEED-POSITION',
      title: 'Sandbox position',
      path: `/positions/${defaults.positionId}`,
      nameOf: (row) => row.title || row.name,
    },
    {
      id: 'SEED-SCHEDULE',
      title: 'Primary schedule',
      path: `/schedules/${defaults.scheduleId}`,
      nameOf: (row) => row.name,
    },
  ];
  if (defaults.locationId2 && defaults.locationId2 !== defaults.locationId) {
    plan.push({
      id: 'SEED-LOCATION-2',
      title: 'Second location',
      path: `/locations/${defaults.locationId2}`,
      nameOf: (row) => row.name,
    });
  }
  if (defaults.scheduleId2 && defaults.scheduleId2 !== defaults.scheduleId) {
    plan.push({
      id: 'SEED-SCHEDULE-2',
      title: 'Second schedule',
      path: `/schedules/${defaults.scheduleId2}`,
      nameOf: (row) => row.name,
    });
  }
  return plan;
}

function prepareRowsPending(defaults?: Partial<QaTimecardDefaults>): QaCheckRow[] {
  const seedRows = requiredSeedPlan(defaults || {}).map((item) => ({
    id: item.id,
    title: item.title,
    group: 'Prepare',
    status: 'pending',
    message: 'Use existing record if present; do not create a duplicate.',
  }));
  const extra = [
    ['SEED-CATALOG', 'Every location, schedule, and position used by scenarios', 'Fail here if any scenario ID is missing.'],
    ['SEED-WEEK', 'Timecard week start day', 'Scenario weeks are aligned to this weekday so sync does not drop rows.'],
    ['SEED-ASSIGN', 'Employee location assignment', 'Assign sandbox locations only if they are missing.'],
    ['SEED-SCHEDULE-ASSIGN', 'Employee schedule assignment', 'Clock-in is rejected unless these schedules are on the profile.'],
    ['SEED-POSITION-ASSIGN', 'Employee position assignment', 'Assign sandbox position only if it is missing.'],
    ['SEED-ROLE', 'Employee app role', 'Assign a role only if the profile has none.'],
    ['SEED-BREAK', 'Break settings', 'Load tenant break policy. Early tests set and restore their own brackets.'],
    ['SEED-CLOCK', 'Timecard clock in/out rules', 'KDOC late-out uses schedule time; tests that need punch time set it and restore.'],
    ['SEED-COMPANY', 'Company settings', 'Required for schedule and timecard rules.'],
    ['PREP-CLEANUP', 'Cleanup leftover sandbox punches and shifts', 'Wipe previous scenario data, then run tests.'],
    ['SEED-PROBE', 'Trial shift and punch', 'Write one shift and punch; if this fails, tests do not run.'],
  ];
  for (const [id, title, message] of extra) {
    seedRows.push({ id, title, group: 'Prepare', status: 'pending', message });
  }
  return seedRows;
}

function emit(onRow, id, title, status, message) {
  onRow({ id, title, group: 'Prepare', status, message });
}

async function readExisting(client, path, nameOf, label) {
  const response = await client.get(path);
  if (response.status === 404) {
    return { ok: false, name: null, data: null, message: `${label} is missing — tests need this seed` };
  }
  const data = await expectOk(response, `GET ${path}`);
  const name = nameOf(data) || label;
  return {
    ok: true,
    name,
    data,
    message: `${name} already present — not created`,
  };
}

async function verifyCatalogIds(client, ids, onRow) {
  emit(onRow, 'SEED-CATALOG', 'Every location, schedule, and position used by scenarios', 'running', '');
  const missing = [];
  const present = [];

  async function check(kind, id, path, nameOf) {
    const result = await readExisting(client, path, nameOf, `${kind} ${id}`);
    if (result.ok) present.push(`${kind} ${result.name}`);
    else missing.push(`${kind} ${id}`);
  }

  try {
    for (const id of ids.locationIds) {
      await check('location', id, `/locations/${id}`, (row) => row.name);
    }
    for (const id of ids.scheduleIds) {
      await check('schedule', id, `/schedules/${id}`, (row) => row.name);
    }
    for (const id of ids.positionIds) {
      await check('position', id, `/positions/${id}`, (row) => row.title || row.name);
    }
    if (missing.length > 0) {
      emit(
        onRow,
        'SEED-CATALOG',
        'Every location, schedule, and position used by scenarios',
        'failed',
        `Missing: ${missing.join(', ')}`
      );
      return false;
    }
    emit(
      onRow,
      'SEED-CATALOG',
      'Every location, schedule, and position used by scenarios',
      'passed',
      `Already present — ${present.join('; ')}`
    );
    return true;
  } catch (err) {
    emit(
      onRow,
      'SEED-CATALOG',
      'Every location, schedule, and position used by scenarios',
      'failed',
      err instanceof Error ? err.message : String(err)
    );
    return false;
  }
}

async function verifyRequiredSeed(client, defaults, scenarios, onRow, config) {
  emit(onRow, 'SEED-WEEK', 'Timecard week start day', 'running', '');
  try {
    const response = await client.get('/system-settings/week_start_day');
    if (response.status >= 200 && response.status < 300 && response.data?.value) {
      defaults.weekStartsOn = String(response.data.value).trim().toLowerCase();
    } else {
      defaults.weekStartsOn = defaults.weekStartsOn || 'monday';
    }
    emit(
      onRow,
      'SEED-WEEK',
      'Timecard week start day',
      'passed',
      `Week starts ${defaults.weekStartsOn}. Scenario week headers will be aligned to that day.`
    );
  } catch (err) {
    defaults.weekStartsOn = defaults.weekStartsOn || 'monday';
    emit(
      onRow,
      'SEED-WEEK',
      'Timecard week start day',
      'passed',
      `Could not read setting; assuming ${defaults.weekStartsOn}. ${err instanceof Error ? err.message : String(err)}`
    );
  }

  const plan = requiredSeedPlan(defaults);
  const ids = collectRequiredIds(defaults, scenarios, config);
  let ok = true;
  let employee = null;

  for (const item of plan) {
    emit(onRow, item.id, item.title, 'running', '');
    try {
      const result = await readExisting(client, item.path, item.nameOf, item.title);
      if (item.id === 'SEED-EMPLOYEE') employee = result.data;
      if (!result.ok) ok = false;
      emit(onRow, item.id, item.title, result.ok ? 'passed' : 'failed', result.message);
    } catch (err) {
      ok = false;
      emit(onRow, item.id, item.title, 'failed', err instanceof Error ? err.message : String(err));
    }
  }

  const catalogOk = await verifyCatalogIds(client, ids, onRow);
  if (!catalogOk) ok = false;

  const skipAssign = (id, title) =>
    emit(onRow, id, title, 'failed', 'Skipped — sandbox employee is missing');

  if (!employee) {
    skipAssign('SEED-ASSIGN', 'Employee location assignment');
    skipAssign('SEED-SCHEDULE-ASSIGN', 'Employee schedule assignment');
    skipAssign('SEED-POSITION-ASSIGN', 'Employee position assignment');
    skipAssign('SEED-ROLE', 'Employee app role');
    emit(onRow, 'SEED-BREAK', 'Break settings', 'skipped', 'Skipped — sandbox employee is missing');
    emit(onRow, 'SEED-CLOCK', 'Timecard clock in/out rules', 'skipped', 'Skipped — sandbox employee is missing');
    emit(onRow, 'SEED-COMPANY', 'Company settings', 'skipped', 'Skipped — sandbox employee is missing');
    emit(onRow, 'SEED-PROBE', 'Trial shift and punch', 'skipped', 'Skipped — sandbox employee is missing');
    return { ok: false, employeeOk: false, ids };
  }

  if (!employee.isActive) {
    ok = false;
    emit(onRow, 'SEED-EMPLOYEE', 'Sandbox employee', 'failed', `${personName(employee)} exists but is inactive`);
  }

  emit(onRow, 'SEED-ASSIGN', 'Employee location assignment', 'running', '');
  try {
    const needed = uniqueNums(ids.locationIds);
    const existing = employee.profile?.subLocationIds || [];
    const main = employee.profile?.mainLocationId;
    const missing = needed.filter((id) => id !== main && !existing.includes(id));
    if (missing.length === 0) {
      emit(onRow, 'SEED-ASSIGN', 'Employee location assignment', 'passed', 'Location assignment already present — not changed');
    } else {
      await ensureEmployeeSubLocations(client, defaults.employeeId, needed, defaults.defaultAppRoleId ?? 3);
      emit(
        onRow,
        'SEED-ASSIGN',
        'Employee location assignment',
        'passed',
        'Added missing location assignment(s); left existing assignments in place'
      );
    }
  } catch (err) {
    ok = false;
    emit(onRow, 'SEED-ASSIGN', 'Employee location assignment', 'failed', err instanceof Error ? err.message : String(err));
  }

  emit(onRow, 'SEED-SCHEDULE-ASSIGN', 'Employee schedule assignment', 'running', '');
  try {
    const result = await ensureEmployeeSchedules(client, defaults.employeeId, ids.scheduleIds);
    emit(
      onRow,
      'SEED-SCHEDULE-ASSIGN',
      'Employee schedule assignment',
      'passed',
      result.added
        ? 'Added missing schedule assignment(s); left existing assignments in place'
        : 'Schedule assignment already present — not changed'
    );
  } catch (err) {
    ok = false;
    emit(onRow, 'SEED-SCHEDULE-ASSIGN', 'Employee schedule assignment', 'failed', err instanceof Error ? err.message : String(err));
  }

  emit(onRow, 'SEED-POSITION-ASSIGN', 'Employee position assignment', 'running', '');
  try {
    const result = await ensureEmployeePositions(client, defaults.employeeId, ids.positionIds);
    emit(
      onRow,
      'SEED-POSITION-ASSIGN',
      'Employee position assignment',
      'passed',
      result.added
        ? 'Added missing position assignment(s); left existing assignments in place'
        : 'Position assignment already present — not changed'
    );
  } catch (err) {
    ok = false;
    emit(onRow, 'SEED-POSITION-ASSIGN', 'Employee position assignment', 'failed', err instanceof Error ? err.message : String(err));
  }

  emit(onRow, 'SEED-ROLE', 'Employee app role', 'running', '');
  try {
    const result = await ensureEmployeeAppRole(client, defaults.employeeId, defaults.defaultAppRoleId ?? 3);
    emit(
      onRow,
      'SEED-ROLE',
      'Employee app role',
      'passed',
      result.added ? 'Assigned app role; profile had none' : 'App role already present — not changed'
    );
  } catch (err) {
    ok = false;
    emit(onRow, 'SEED-ROLE', 'Employee app role', 'failed', err instanceof Error ? err.message : String(err));
  }

  emit(onRow, 'SEED-BREAK', 'Break settings', 'running', '');
  try {
    const settings = await getBreakSettings(client);
    const rules = Array.isArray(settings?.rules) ? settings.rules : [];
    const ruleSummary = rules
      .map((r) => `${r.minHours}-${r.maxHours}h=${r.breakMinutes}m`)
      .join(', ');
    emit(
      onRow,
      'SEED-BREAK',
      'Break settings',
      'passed',
      `isPaid=${!!settings?.isPaid}, ${rules.length} rule(s)${ruleSummary ? ` (${ruleSummary})` : ''}. Tests that need brackets apply them per scenario and restore.`
    );
  } catch (err) {
    ok = false;
    emit(onRow, 'SEED-BREAK', 'Break settings', 'failed', err instanceof Error ? err.message : String(err));
  }

  emit(onRow, 'SEED-CLOCK', 'Timecard clock in/out rules', 'running', '');
  try {
    const clock = await getClockSettings(client);
    const lateOut = clock.timecard_clock_out_late || 'unset';
    emit(
      onRow,
      'SEED-CLOCK',
      'Timecard clock in/out rules',
      'passed',
      `clock_out_late=${lateOut}, clock_in_early=${clock.timecard_clock_in_early || 'unset'}. Tests that need punch-time late-out set it per scenario and restore.`
    );
  } catch (err) {
    ok = false;
    emit(onRow, 'SEED-CLOCK', 'Timecard clock in/out rules', 'failed', err instanceof Error ? err.message : String(err));
  }

  emit(onRow, 'SEED-COMPANY', 'Company settings', 'running', '');
  try {
    const response = await client.get('/companysettings');
    const data = await expectOk(response, 'GET /companysettings');
    const name = data?.companyName?.trim();
    emit(
      onRow,
      'SEED-COMPANY',
      'Company settings',
      'passed',
      name ? `Company settings loaded (${name})` : 'Company settings loaded'
    );
  } catch (err) {
    ok = false;
    emit(onRow, 'SEED-COMPANY', 'Company settings', 'failed', err instanceof Error ? err.message : String(err));
  }

  return { ok, employeeOk: true, ids };
}

async function cleanupSandboxData(client, defaults, scenarios, onRow) {
  const weeks = collectSandboxWeeks(defaults, scenarios);
  emit(onRow, 'PREP-CLEANUP', 'Cleanup leftover sandbox punches and shifts', 'running', `Cleaning weeks ${weeks.join(', ')}`);

  let shiftsRemoved = 0;
  let attendanceRemoved = 0;
  try {
    for (const week of weeks) {
      const result = await cleanupWeek(client, defaults.employeeId, week, defaults.locationId);
      shiftsRemoved += result.shiftsRemoved || 0;
      attendanceRemoved += result.attendanceRemoved || 0;
      if (defaults.locationId2 && defaults.locationId2 !== defaults.locationId) {
        const other = await cleanupWeek(client, defaults.employeeId, week, defaults.locationId2);
        shiftsRemoved += other.shiftsRemoved || 0;
        attendanceRemoved += other.attendanceRemoved || 0;
      }
    }
    emit(
      onRow,
      'PREP-CLEANUP',
      'Cleanup leftover sandbox punches and shifts',
      'passed',
      `Removed ${shiftsRemoved} shift(s) and ${attendanceRemoved} punch(es) from weeks ${weeks.join(', ')}`
    );
    return { ok: true };
  } catch (err) {
    emit(
      onRow,
      'PREP-CLEANUP',
      'Cleanup leftover sandbox punches and shifts',
      'failed',
      err instanceof Error ? err.message : String(err)
    );
    return { ok: false };
  }
}

async function probeSandboxWrite(client, defaults, onRow) {
  const date = getWeekStartForDate(
    defaults.sandboxWeekStart || defaults.weekStart,
    defaults.weekStartsOn || 'monday'
  );
  emit(onRow, 'SEED-PROBE', 'Trial shift and punch', 'running', `Writing a trial shift and punch on ${date}`);
  try {
    await createShift(
      client,
      {
        date,
        start: '06:00',
        end: '06:30',
        breakUseGlobalSettings: false,
        breakTimePaid: true,
        unpaidHours: 0.5,
      },
      defaults
    );
    await createAttendance(
      client,
      { date, clockIn: '06:00', clockOut: '06:30', scheduleId: defaults.scheduleId },
      defaults
    );
    if (defaults.locationId2 && defaults.locationId2 !== defaults.locationId) {
      await createAttendance(
        client,
        {
          date,
          clockIn: '07:00',
          clockOut: '07:30',
          scheduleId: defaults.scheduleId,
          locationId: defaults.locationId2,
        },
        defaults
      );
    }
    await hardSync(client, defaults.employeeId, date, defaults.locationId);
    const timecard = await getTimecard(client, defaults.employeeId, date, defaults.locationId);
    if (!timecard?.days?.length) {
      throw new Error(
        `Trial sync produced 0 timecard rows for week ${date}. Tenant week start is ${defaults.weekStartsOn || 'monday'}.`
      );
    }
    const paidDay = (timecard.days || []).find((day) => day.paidBreakPaid);
    if (!paidDay) {
      throw new Error(
        `Trial paid-break shift did not set paidBreakPaid. Company default is unpaid; shift override must win. Actual: ${JSON.stringify(
          (timecard.days || []).map(summarizeDay)
        )}`
      );
    }
    emit(
      onRow,
      'SEED-PROBE',
      'Trial shift and punch',
      'passed',
      `Trial shift, punch, and sync succeeded (${timecard.days.length} row(s) on week ${date}; paidBreakPaid=true)`
    );
    return true;
  } catch (err) {
    emit(
      onRow,
      'SEED-PROBE',
      'Trial shift and punch',
      'failed',
      err instanceof Error ? err.message : String(err)
    );
    return false;
  }
}

async function prepareSandbox(
  client: AxiosInstance,
  defaults: QaTimecardDefaults,
  scenarios: TimecardScenario[],
  onRow: (row: QaCheckRow) => void,
  config?: { defaults: QaTimecardDefaults }
) {
  const seed = await verifyRequiredSeed(client, defaults, scenarios, onRow, config);
  if (!seed.employeeOk) {
    emit(onRow, 'PREP-CLEANUP', 'Cleanup leftover sandbox punches and shifts', 'skipped', 'Skipped cleanup — sandbox employee is missing');
    emit(onRow, 'SEED-PROBE', 'Trial shift and punch', 'skipped', 'Skipped — sandbox employee is missing');
    return { seedOk: false, cleanupOk: false };
  }

  const cleanup = await cleanupSandboxData(client, defaults, scenarios, onRow);
  if (!seed.ok || !cleanup.ok) {
    emit(onRow, 'SEED-PROBE', 'Trial shift and punch', 'skipped', 'Skipped — fix seed or cleanup first');
    return { seedOk: false, cleanupOk: cleanup.ok };
  }

  const probeOk = await probeSandboxWrite(client, defaults, onRow);
  const cleanupAfterProbe = await cleanupSandboxData(client, defaults, scenarios, onRow);
  return {
    seedOk: probeOk && cleanupAfterProbe.ok,
    cleanupOk: cleanupAfterProbe.ok,
  };
}

export {
  collectSandboxWeeks,
  collectRequiredIds,
  requiredSeedPlan,
  prepareRowsPending,
  prepareSandbox,
  weekStartOnOrBefore,
};
